import React, {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import jexcel, { JExcelElement } from "jexcel";

import {
  IClass,
  ISchoolManager,
  IStudent,
  IStudentForRegister,
} from "../../../state";
import { ModalContext } from "../../../libs/ModalProvider";
import Confirm from "../../../molecules/Confirm";
import useCurrentYear from "../../../hooks/manager/useCurrentYear";

import styles from "./BulkStudentForm.module.scss";
export interface BulkStudentFormProps {
  title: string;
  classes?: IClass[];
  error: string;
  student?: IStudent;
  isNextYear: boolean;
  calling: boolean;
  next_classes?: IClass[];
  school: ISchoolManager;
  save: (students: IStudentForRegister[]) => void;
}

export const BulkStudentForm = (props: BulkStudentFormProps) => {
  const ctx = React.useContext(ModalContext);
  const { currentYear, nextYear, nextYearShortLabel } = useCurrentYear();
  const entranceYearSelection: number[] = !props.isNextYear
    ? [-5, -4, -3, -2, -1, 0]
    : [1];
  const disabled =
    props.isNextYear &&
    props.student &&
    props.student.entrance_year <= currentYear;
  const jexcelRef = useRef<HTMLDivElement>(null);
  const CSVInputRef = useRef<HTMLInputElement>(null);
  const cols = useMemo(() => {
    const res = {
      last_name: { index: 0, title: "名字 ※必須" },
      first_name: { index: 1, title: "名前 ※必須" },
      last_name_kana: { index: 2, title: "名字(カナ) ※必須" },
      first_name_kana: { index: 3, title: "名前(カナ) ※必須" },
      student_no_in_school: { index: 4, title: "学籍番号 ※任意" },
      email: { index: 5, title: "メールアドレス ※任意" },
      guardian_email: { index: 6, title: "保護者のメールアドレス ※任意" },
    } as {
      [key: string]: { index: number; title: string; excelTitle?: string };
    };

    return res;
  }, []);

  const exportCSV = useCallback(() => {
    const headers = Object.values(cols).map(col => col.title);
    const exampleRow = [
      "例：山田", // last_name
      "例：太郎", // first_name
      "例：ヤマダ", // last_name_kana
      "例：タロウ", // first_name_kana
      "", // student_no_in_school
      "", // email
      "", // guardian_email
    ];

    // Build CSV content
    const csvContent = headers.join(",") + "\n" + exampleRow.join(",") + "\n";

    // Create Blob and download link
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "new_student_list.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [cols]);
  const current_grade = !props.isNextYear
    ? sessionStorage.getItem("current_grade")
      ? parseInt(sessionStorage.getItem("current_grade") as string, 10)
      : 1
    : 0;
  const next_grade = current_grade + 1;
  const entrance_year = !props.isNextYear
    ? currentYear - current_grade + 1
    : nextYear;

  const [state, setState] = useState({
    csvFile: null as null | File,
    g4s_enabled: props.student ? props.student.g4s_enabled : false,
    dlp_enabled: props.student
      ? props.student.dlp_enabled
      : props.school.total_dlp_registrable_num! > 0,
    class_id: props.student ? props.student.class_id : 0,
    next_klass_id: props.student ? props.student.next_klass_id : 0,
    entrance_year: props.student ? props.student.entrance_year : entrance_year,
    current_grade: props.student ? props.student.current_grade : current_grade,
    next_grade: props.student ? props.student.next_grade : next_grade,
    sheet: null as null | JExcelElement,
    errorMessages: {} as { [key: string]: string },
    isSubmitted: false,
  });

  const resetFunc = useCallback(() => {
    CSVInputRef.current && (CSVInputRef.current.value = "");
    setState(prevState => ({
      ...prevState,
      csvRowsData: null,
      csvFile: null,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showErrorMessage = useCallback(
    (message: string) => {
      resetFunc();
      ctx.actions.showModal(
        <Confirm
          content={message}
          closeModal={ctx.actions.closeModal}
          callback={ctx.actions.closeModal}
        />,
      );
    },
    [ctx.actions, resetFunc],
  );

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const key = e.currentTarget.name;
      const val = e.currentTarget.type.match(/select/)
        ? parseInt(e.currentTarget.value, 10)
        : e.currentTarget.type === "radio"
        ? e.currentTarget.value === "true"
          ? true
          : e.currentTarget.value === "false"
          ? false
          : parseInt(e.currentTarget.value, 10)
        : e.currentTarget.type === "checkbox"
        ? parseInt(e.currentTarget.value, 10) ===
          (state as { [key: string]: any })[key]
          ? 0
          : parseInt(e.currentTarget.value, 10)
        : e.currentTarget.value;
      const errorMessages = {} as { [key: string]: string };
      setState(prevState => {
        const args = { ...prevState, [key]: val };
        return {
          ...args,
          errorMessages,
        };
      });
    },
    [state],
  );

  const handleImportCSV = (event: ChangeEvent<HTMLInputElement>) => {
    let file = event.target?.files ? event.target?.files[0] : null;
    if (!file || !state.sheet) return;

    const reader = new FileReader();
    reader.readAsText(file);

    reader.onload = e => {
      try {
        // Clear the jexcel sheet on file load
        const totalRows = state.sheet?.getData().length || 0;
        if (totalRows > 1) {
          for (let i = totalRows - 1; i >= 0; i--) {
            state.sheet?.deleteRow(i, totalRows);
          }
        }

        const csvString = e.target?.result as string;

        // Parse CSV into rows and cells
        let rows = csvString
          .trim()
          .split("\n")
          .map(row => row.split(",").map(cell => cell.trim()));

        if (!rows || rows.length === 0) {
          showErrorMessage("無効なCSV形式です");
        }

        // Get the header row from the CSV
        const headerRow = rows[0];

        // Expected column titles
        const expectedColumns = Object.values(cols).map(col => col.title);

        // Validate the header row against the expected columns
        const isValidHeader =
          headerRow.length === expectedColumns.length &&
          headerRow.every((col, index) => col === expectedColumns[index]);

        if (!isValidHeader) {
          showErrorMessage(
            "無効な列形式です。適切な列を含む CSV を追加してください",
          );
          file = null;
          return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        rows = rows.slice(1);
        rows.forEach(row => {
          if (!row[0]) {
            showErrorMessage(`「名字」が不足している生徒がいます。`);
            file = null;
          }
          if (!row[1]) {
            showErrorMessage(`「名前」が不足している生徒がいます。`);
            file = null;
          }
          if (!row[2]) {
            showErrorMessage(`「名字(カナ)」が不足している生徒がいます。`);
            file = null;
          }
          if (!row[3]) {
            showErrorMessage(`「名前(カナ)」が不足している生徒がいます。`);
            file = null;
          }
          if (row[5] && !emailRegex.test(row[5])) {
            showErrorMessage(`不正なメールアドレスが入力されています`);
            file = null;
          }
          if (row[6] && !emailRegex.test(row[6])) {
            showErrorMessage(`不正なメールアドレスが入力されています`);
            file = null;
          }
        });

        // Filter rows: Ensure the first 4 columns are non-empty

        rows = rows
          .slice(1)
          .filter(
            row => row.length >= 4 && row[0] && row[1] && row[2] && row[3],
          );

        rows.forEach((row, index) => {
          state.sheet?.insertRow(row, index);
        });

        setState(prevState => ({
          ...prevState,
          csvFile: file,
        }));
      } catch (error) {
        showErrorMessage("何か問題が発生しました。もう一度試してください。");
      }
    };

    reader.onerror = () => {
      showErrorMessage("何か問題が発生しました。もう一度試してください。");
    };
  };

  useEffect(() => {
    if (jexcelRef.current) {
      const jexcelInstance = jexcel(jexcelRef.current, {
        data: [],
        columns: [
          { type: "text", title: "名字" },
          { type: "text", title: "名前" },
          { type: "text", title: "名字(カナ)" },
          { type: "text", title: "名前(カナ)" },
          { type: "text", title: "学籍番号" },
          { type: "text", title: "メールアドレス" },
          { type: "text", title: "保護者のメールアドレス" },
        ],
        minSpareRows: [1],
      });
      setState(prevState => ({ ...prevState, sheet: jexcelInstance }));
    }
  }, []);

  const onSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();

      const data: IStudentForRegister[] = [];
      if (state.sheet) {
        // Extract raw data from the sheet
        let dataSet = state.sheet?.getData();

        if (dataSet) {
          // Skip the header row and last row
          dataSet = dataSet.slice(1, dataSet.length - 1);

          dataSet.forEach(row => {
            data.push({
              identity: "",
              last_name: row[cols["last_name"].index] as string,
              first_name: row[cols["first_name"].index] as string,
              last_name_kana: row[cols["last_name_kana"].index] as string,
              first_name_kana: row[cols["first_name_kana"].index] as string,
              student_no_in_school: row[
                cols["student_no_in_school"].index
              ] as string,
              email: row[cols["email"].index] as string || null,
              guardian_email: row[cols["guardian_email"].index] as string || null,
              entrance_year: state.entrance_year,
              current_grade: state.current_grade || null,
              next_grade: state.current_grade + 1,
              class_id: state.class_id || null,
              next_class_id: state.next_klass_id || null,
              g4s_enabled: state.g4s_enabled,
              dlp_enabled: state.dlp_enabled,
              plan_ids: null,
            });
          });
        }
      }
      props.save(data);
    },

    [cols, state, props],
  );

  const isDisabled = () => {
    if (props.calling === true) {
      return true;
    }

    if (!state.csvFile) {
      return true;
    }

    if (props.isNextYear && !state.next_klass_id) {
      return true;
    }

    return false;
  };

  return (
    <div className="container">
      <div className={styles.pageTitle}>{props.title}</div>
      <div ref={jexcelRef} className="jexcel-container d-none" />
      <p className="d-flex justify-content-center mt-3">
        新規で登録する生徒のみをCSVに含めてください
      </p>
      <div className={styles.inputAreaWrapper}>
        <form
          onSubmit={onSubmit}
          className={state.isSubmitted ? "was-validated" : ""}
        >
          {props.error && (
            <div className={styles.errorBillboard}>{props.error}</div>
          )}
          <div className="mb-3">
            <label
              className={`form-label ${styles.required}`}
              htmlFor="entrance_year"
            >
              入学年度
            </label>
            <select
              name="entrance_year"
              className="form-select"
              aria-label="select"
              disabled={disabled}
              value={state.entrance_year}
              onChange={onChange}
            >
              {entranceYearSelection.map(m => {
                return (
                  <option key={m} value={m + currentYear}>
                    {m + currentYear}年
                  </option>
                );
              })}
            </select>
          </div>
          {(!props.isNextYear ||
            (props.student && props.student.entrance_year <= currentYear)) &&
            props.classes && (
              <>
                <div className="mb-3">
                  <label
                    className={`form-label ${styles.required}`}
                    htmlFor="current_grade"
                  >
                    現在の学年
                  </label>
                  <select
                    name="current_grade"
                    className="form-select"
                    aria-label="select"
                    disabled={disabled}
                    value={state.current_grade ?? undefined}
                    onChange={e => {
                      setState({
                        ...state,
                        class_id: 0,
                        next_grade: parseInt(e.currentTarget.value, 10) + 1,
                      });
                      onChange(e);
                    }}
                  >
                    {[1, 2, 3, 4, 5, 6].map(m => {
                      return (
                        <option key={m} value={m}>
                          {m}年
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="mb-3">
                  <label className={`form-label`} htmlFor="class_id">
                    クラス
                  </label>
                  <div id="students" className={styles.selectArea}>
                    {props.classes
                      .filter(c => c.grade === state.current_grade)
                      .map(c => {
                        return (
                          <label className={styles.selectBtn} key={c.id}>
                            <input
                              type="checkbox"
                              name="class_id"
                              checked={state.class_id === c.id}
                              disabled={disabled}
                              value={c.id}
                              onChange={onChange}
                            />
                            <span>{c.full_name}</span>
                          </label>
                        );
                      })}
                  </div>
                </div>
              </>
            )}

          {props.isNextYear && props.next_classes && (
            <>
              <div className="mb-3">
                <label
                  className={`form-label ${styles.required}`}
                  htmlFor="next_grade"
                >
                  {`${nextYearShortLabel}年度学年`}
                </label>
                <select
                  name="next_grade"
                  className="form-select"
                  aria-label="select"
                  value={state.next_grade}
                  onChange={onChange}
                >
                  {[1, 2, 3, 4, 5, 6].map(m => {
                    return (
                      <option key={m} value={m}>
                        {m}年
                      </option>
                    );
                  })}
                </select>
              </div>
              <label
                className={`form-label ${styles.required}`}
                htmlFor="next_klass_id"
              >
                {nextYearShortLabel}年度クラス
              </label>
              <div className={styles.selectArea}>
                {props.next_classes
                  .filter(c => c.grade === state.next_grade)
                  .map(c => (
                    <label className={styles.selectBtn} key={c.id}>
                      <input
                        type="radio"
                        name="next_klass_id"
                        checked={state.next_klass_id === c.id}
                        value={c.id}
                        onChange={onChange}
                      />
                      <span>{c.full_name}</span>
                    </label>
                  ))}
              </div>
            </>
          )}

          {props.school!.total_dlp_registrable_num! > 0 && (
            <div className="mb-3">
              <label className={`${styles.note}`}>
                ※納品指定日より前に生徒登録を行う場合、「教材対象」を無効で登録ください。納品後に改めて一括編集機能により「教材対象」の更新が可能です。
              </label>
              <span className={`${styles.label} ${styles.required}`}>
                教材対象
              </span>
              <div className="form-check form-check-inline">
                <input
                  type="radio"
                  className="form-check-input"
                  name="dlp_enabled"
                  id="dlp_enabled"
                  checked={state.dlp_enabled}
                  value={"true"}
                  onChange={onChange}
                />
                <label className="form-check-label" htmlFor="dlp_enabled">
                  有効
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  type="radio"
                  className="form-check-input"
                  name="dlp_enabled"
                  id="dlp_disabled"
                  checked={!state.dlp_enabled}
                  value={"false"}
                  onChange={onChange}
                />
                <label className="form-check-label" htmlFor="dlp_disabled">
                  無効
                </label>
              </div>
            </div>
          )}
          {props.school.total_g4s_registrable_num! > 0 && (
            <div className="mb-3">
              <span className={`${styles.label} ${styles.required}`}>
                GEAR拡張
              </span>
              <div className="form-check form-check-inline">
                <input
                  type="radio"
                  className="form-check-input"
                  name="g4s_enabled"
                  id="g4s_enabled"
                  checked={state.g4s_enabled}
                  value={"true"}
                  onChange={onChange}
                />
                <label className="form-check-label" htmlFor="g4s_enabled">
                  有効
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  type="radio"
                  className="form-check-input"
                  name="g4s_enabled"
                  id="g4s_enabled"
                  checked={!state.g4s_enabled}
                  value={"false"}
                  onChange={onChange}
                />
                <label className="form-check-label" htmlFor="g4s_enabled">
                  無効
                </label>
              </div>
            </div>
          )}
          <div className="mb-3 d-flex align-items-center">
            <label className={`me-3 ${styles.required}`} htmlFor="file_upload">
              生徒データ
            </label>
            <div className={`${styles.actionButtons} align-items-center`}>
              <button
                type="button"
                className={`btn btn-outline-primary me-3 ${styles.importCsv}`}
                onClick={() => CSVInputRef.current?.click()}
              >
                CSVインポート
                <input
                  ref={CSVInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleImportCSV}
                />
              </button>
            </div>
          </div>
          {state.csvFile && (
            <p className={styles.fileName}>{state.csvFile.name}</p>
          )}
          <div className="mb-3 ">
            <label className={` ${styles.note}`}>
              ※生徒データは以下の登録用CSVフォーマットをダウンロードして、登録する生徒の情報を追記してください
            </label>
            <div className={styles.actionButtons}>
              <button
                className={`btn btn-outline me-3 ${styles.utilBtn} flex-grow-1 text-nowrap `}
                onClick={() =>
                  ctx.actions.showModal(
                    <Confirm
                      content={
                        <span>
                          生徒データの登録用CSVフォーマットをダウンロードします。
                          <br />
                          サンプルの記入例が記載してあるので、そちらを参考に登録したい生徒の内容を記載してください。
                        </span>
                      }
                      closeModal={() => {
                        ctx.actions.closeModal();
                      }}
                      callback={exportCSV}
                    />,
                  )
                }
                type="button"
              >
                登録用CSVフォーマットのダウンロード
              </button>
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={onSubmit}
            type="submit"
            disabled={isDisabled()}
          >
            登録
          </button>
        </form>
      </div>
    </div>
  );
};
